// TelcoTrack - GeoJSON Import Modal Component
// Modal for importing GeoJSON files with layer mapping and exploring existing layers

import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ChangeDetectorRef,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import type * as L from 'leaflet';
import { ObjectService } from '../../../core/services/object.service';
import { PolygonService } from '../../../core/services/polygon.service';
import { LoggerService } from '../../../core/services/logger.service';
import type { Polygon } from '../../../core/models/polygon.model';
import type { StepperStep } from '../ui/stepper/stepper.component';
import {
  DRAWING_TYPE_MAPPING,
  POLYGON_CONSTRUCTION_TYPES,
  POLYGON_RESPONSIBLE_BRANCHES,
  POLYGON_PLAN_STATUSES,
  DEFAULT_POLYGON_DIARY_STAVEBNIK,
  DEFAULT_POLYGON_DIARY_ZHOTOVITEL,
  isGeojsonImportPolygonPickerEnabled,
  buildAuthorityCatalogApplyByPolygon,
  isDxfFileName,
  isDgnFileName,
  isVykopSegmentKind,
  VYKOP_SEGMENT_KIND_FILTER_LABELS,
  VYKOP_SEGMENT_KIND_FILTER_ORDER,
  decodeCzechTextBuffer,
  type ResolvedImportSourceCrs,
  type VykopSegmentKind,
  projectDriveMissingIdentityFields,
} from '@core/shared-bridge';
import type { FilterMultiOption } from '../../utils/filter-multi.util';
import { getPolygonDisplayLabel, toPolygonFilterOptions } from '../../utils/polygon-display.util';
import {
  importLayerPreviewColor,
  type ImportPreviewFeatureCollection,
} from '../../utils/import-geometry-preview.util';
import { ImportLayerMapOverlayHost } from '../../utils/import-layer-map-overlay.host';
import { analyzeGeoJsonClientSide } from '../../utils/import-geojson-analyze.util';
import { GeometryImportHandoffService } from '../../services/geometry-import-handoff.service';
import { isSafeAppReturnUrl } from '../../utils/app-return-url.util';

interface GeoJsonFeature {
  type: 'Feature';
  geometry: {
    type: 'Point' | 'LineString' | 'Polygon' | 'MultiPoint' | 'MultiLineString' | 'MultiPolygon';
    coordinates: number[] | number[][] | number[][][];
  };
  properties: Record<string, unknown>;
}

interface GeoJsonFeatureCollection {
  type: 'FeatureCollection';
  features: GeoJsonFeature[];
}

interface GeoJsonAnalysisResponse {
  layers: Array<{
    layerName: string;
    featureCount: number;
    geometryTypes: string[];
    sampleProperties: Record<string, unknown>;
  }>;
  totalFeatures: number;
  detectedSourceCrs?: ResolvedImportSourceCrs;
  sourceCrs?: ResolvedImportSourceCrs;
  geoJsonData?: GeoJsonFeatureCollection;
  /** Per-layer sample when the DXF is too large to return as `geoJsonData`. */
  previewGeoJson?: GeoJsonFeatureCollection;
}

interface LayerInfo {
  layerName: string;
  featureCount: number;
  geometryTypes: string[];
  sampleProperties: Record<string, unknown>;
  /**
   * Import UI assignment: object type (BUDOVA, …) or VYKOP segment kind (R100 / MET / UR).
   * Payload maps R100|MET|UR → objectType VYKOP + vykopSegmentKindByLayer.
   */
  mappedType?: string;
  /** Show layer on the mapping-step map preview (default true). */
  visibleOnMap?: boolean;
}

interface ImportResult {
  success: boolean;
  message: string;
  polygonId?: string;
  authorityCatalog?: {
    applied: boolean;
    created: number;
    skipped: number;
    error: string | null;
  };
  statistics: {
    total: number;
    imported: number;
    skipped: number;
    failed: number;
  };
  details?: {
    failed?: Array<{ name: string; error: string }>;
  };
}

interface ImportRequestBase {
  drawingType: '1' | '2';
  polygonId?: string;
  polygonName?: string;
  kiaCode?: string;
  realizationYear?: string;
  folderNumber?: string;
  constructionType?: string;
  expectedConstructionStart?: string;
  plannedConstructionEnd?: string;
  description?: string;
  responsibleBranch?: string;
  sapConstruction?: string;
  projectState?: string;
  stavebnik?: string;
  projektant?: string;
  zhotovitel?: string;
  stavbyveduciName?: string;
  stavebniPovoleniCislo?: string;
  obec?: string;
  fileName?: string;
  layerMapping?: Record<string, string>;
  sourceCrs?: ResolvedImportSourceCrs | 'auto';
  applyAuthorityCatalog?: boolean;
  applyAuthorityCatalogByPolygon?: Record<string, boolean>;
  runAuthorityAutoDoss?: boolean;
  vykopSegmentKindByLayer?: Record<string, string>;
}

/** Import payload: GeoJSON FeatureCollection or raw DXF text (exactly one). */
type ImportRequest =
  | (ImportRequestBase & { geoJsonData: GeoJsonFeatureCollection; dxfData?: never })
  | (ImportRequestBase & { dxfData: string; geoJsonData?: never });

// Explore mode interfaces
interface ExploreLayerInfo {
  layerName: string;
  objectType: string;
  totalCount: number;
  modifiedCount: number;
  drawingType: string;
  importId?: string;
  fileName?: string;
  newObjectType?: string; // For reassignment
  skipped?: boolean; // True if layer was not imported (set to UNASSIGNED)
}

interface ModifiedObject {
  id: string;
  name: string;
  status: string;
  statusName: string;
  photoCount: number;
  updatedAt: Date;
}

interface ModifiedObjectsCheck {
  layerName: string;
  totalCount: number;
  modifiedCount: number;
  unmodifiedCount: number;
  modifiedObjects: ModifiedObject[];
}

/** Side panel width used for Leaflet fitBounds padding on the live map. */
const IMPORT_MAPPING_PANEL_WIDTH_PX = 448;

@Component({
  selector: 'app-import-modal',
  templateUrl: './import-modal.component.html',
  styleUrls: ['./import-modal.component.scss']
})
export class ImportModalComponent implements OnChanges, OnInit, OnDestroy {
  /** Highlighted layer on map preview (hover / click). */
  previewHighlightLayerName: string | null = null;
  /** One-shot fit request for map preview. */
  previewFitLayerName: string | null = null;

  /** Status text for map-hosted overlay build. */
  hostOverlayHint: string | null = null;

  private readonly hostOverlay = new ImportLayerMapOverlayHost();

  layerPreviewColor(index: number): string {
    return importLayerPreviewColor(index);
  }

  /** CAD `properties.Layer` (or analyzed layerName) for the side-panel list. */
  originalCadLayerName(layer: LayerInfo): string {
    const fromSample = layer.sampleProperties?.['Layer'];
    const sampleText = fromSample == null ? '' : String(fromSample).trim();
    if (sampleText) return sampleText;
    return (layer.layerName || '').trim() || 'UNNAMED_LAYER';
  }

  onPreviewLayerHover(layerName: string | null): void {
    this.previewHighlightLayerName = layerName;
    if (this.isMapHostedMappingActive) {
      this.hostOverlay.setHighlight(layerName);
    }
  }

  onPreviewLayerSelect(layerName: string): void {
    this.previewHighlightLayerName = layerName;
    this.previewFitLayerName = layerName;
    if (this.isMapHostedMappingActive) {
      this.hostOverlay.setHighlight(layerName);
      this.hostOverlay.fitToLayer(layerName);
      this.previewFitLayerName = null;
    }
  }

  onPreviewFitHandled(): void {
    this.previewFitLayerName = null;
  }

  onMapLayerActivated(layerName: string): void {
    this.onPreviewLayerSelect(layerName);
    this.cdr.markForCheck();
  }

  /** Checkbox mutates LayerInfo in place — reassign array so map preview OnChanges runs. */
  onLayerVisibilityChange(): void {
    this.layers = [...this.layers];
    void this.syncHostOverlays({ forceFit: false });
  }

  /** True when every CAD layer is shown on the map (master toggle checked). */
  get allLayersVisibleOnMap(): boolean {
    return this.layers.length > 0 && this.layers.every((l) => l.visibleOnMap !== false);
  }

  /** Master show/hide for all import layers (map overlays + per-layer checkboxes). */
  toggleAllLayersVisibility(): void {
    if (!this.layers.length) return;
    const show = !this.allLayersVisibleOnMap;
    this.layers = this.layers.map((layer) => ({
      ...layer,
      visibleOnMap: show,
    }));
    void this.syncHostOverlays({ forceFit: show });
  }

  @Input() isOpen = false;
  @Input() mode: 'import' | 'explore' = 'import';
  @Input() polygonId?: string; // For explore mode / locked import target
  /** When set, GeoJSON import is scoped to this polygon — no picker, no „Nové“. */
  @Input() lockPolygonId?: string;
  /** Explore: show only layers from this import file. */
  @Input() scopedImportFileName?: string;
  /**
   * When true (map page), the mapping step docks as a side panel and paints
   * overlays on `hostMap` instead of a mini-map inside the dialog.
   */
  @Input() mapHostedMapping = false;
  /** Live Leaflet map from /maps (required when mapHostedMapping is true). */
  @Input() hostMap: L.Map | null = null;
  @Output() closeEvent = new EventEmitter<void>();
  @Output() importSuccess = new EventEmitter<ImportResult>();
  /** True while map-hosted mapping panel is active (host can adjust chrome). */
  @Output() mappingActiveChange = new EventEmitter<boolean>();

  /** Centered dialog open — hidden during map-hosted mapping so the map stays interactive. */
  get isDialogOpen(): boolean {
    return this.isOpen && !this.isMapHostedMappingActive;
  }

  /** Side panel + live-map overlays for layer assignment. */
  get isMapHostedMappingActive(): boolean {
    return (
      this.mapHostedMapping &&
      this.isOpen &&
      this.mode === 'import' &&
      this.currentStep === 'mapping'
    );
  }

  get showInlineMapPreview(): boolean {
    return this.mode === 'import' && this.currentStep === 'mapping' && !this.mapHostedMapping;
  }

  /** Full GeoJSON when analyze returned it; otherwise the large-DXF preview sample. */
  get mapPreviewGeoJson(): ImportPreviewFeatureCollection | null {
    const source = this.previewGeoJson ?? this.parsedGeoJson;
    if (!source || source.type !== 'FeatureCollection' || !Array.isArray(source.features)) return null;
    return source as ImportPreviewFeatureCollection;
  }

  /** True when the map is showing a sample of a larger DXF. */
  get mapPreviewIsPartial(): boolean {
    const shown = this.mapPreviewGeoJson?.features?.length ?? 0;
    return shown > 0 && this.totalFeatures > shown;
  }

  get showPolygonPicker(): boolean {
    return isGeojsonImportPolygonPickerEnabled(this.lockPolygonId);
  }

  // Step management
  currentStep: 'file' | 'mapping' | 'importing' | 'result' | 'explore' | 'warning' | 'reassigning' = 'file';

  /** Visual progress for `mode === 'import'` (P1 #13) — File → Cíl a mapování → Import → Výsledek. */
  readonly importWizardSteps: StepperStep[] = [
    { id: 'file', label: 'Soubor' },
    { id: 'mapping', label: 'Cíl a mapování' },
    { id: 'importing', label: 'Import' },
    { id: 'result', label: 'Výsledek' },
  ];

  // File selection
  selectedFile: File | null = null;
  fileError: string | null = null;
  parsedGeoJson: GeoJsonFeatureCollection | null = null;
  /**
   * Map preview for a large DXF. Analyze omits full `geoJsonData` past the cap and
   * returns this sample instead; import still sends `pendingDxfText`.
   */
  previewGeoJson: GeoJsonFeatureCollection | null = null;
  /** Raw DXF text (UTF-8 / windows-1250 auto-detect) when uploading .dxf — sent to analyze if client has no GeoJSON yet. */
  private pendingDxfText: string | null = null;

  // Analysis data
  layers: LayerInfo[] = [];
  totalFeatures = 0;
  analysisError: string | null = null;
  isAnalyzing = false;
  /** Auto-detected CRS from analyze (input CRS is never user-overridable). */
  detectedSourceCrs: ResolvedImportSourceCrs | null = null;
  /** Always `auto` — preview / handoff plumbing; import uses `detectedSourceCrs`. */
  selectedSourceCrs: ResolvedImportSourceCrs | 'auto' = 'auto';

  // Import configuration
  drawingType: '1' | '2' = '1';
  kiaCode = '';
  selectedPolygonId: string = '';
  newPolygonName = '';
  newPolygonKiaCode = '';
  newPolygonRealizationYear = '';
  newPolygonFolderNumber = '';
  newPolygonConstructionType = '';
  newPolygonExpectedConstructionStart = '';
  newPolygonPlannedConstructionEnd = '';
  newPolygonDescription = '';
  newPolygonResponsibleBranch = '';
  newPolygonSapConstruction = '';
  newPolygonProjectState = '';
  newPolygonStavebnik = DEFAULT_POLYGON_DIARY_STAVEBNIK;
  newPolygonProjektant = '';
  newPolygonZhotovitel = DEFAULT_POLYGON_DIARY_ZHOTOVITEL;
  newPolygonStavbyveduciName = '';
  newPolygonStavebniPovoleniCislo = '';
  newPolygonObec = '';
  isNewPolygon = false;
  readonly constructionTypeOptions = POLYGON_CONSTRUCTION_TYPES;
  readonly responsibleBranchOptions = [...POLYGON_RESPONSIBLE_BRANCHES];
  readonly projectStateOptions = [...POLYGON_PLAN_STATUSES];

  // Polygons
  polygons: Polygon[] = [];

  importResult: ImportResult | null = null;
  isImporting = false;
  importError: string | null = null;
  /** Progress during background import (imported / total). */
  importProgress = { imported: 0, total: 0, phase: undefined as string | undefined };

  // Available object types for mapping - loaded from backend and filtered by drawingType
  objectTypes: Array<{ value: string; label: string }> = [];
  isLoadingObjectTypes = false;

  // All object types from backend (loaded dynamically, no hardcoded types)
  private allObjectTypes: Array<{ value: string; label: string }> = [];

  // Explore mode state
  exploreLayers: ExploreLayerInfo[] = [];
  filteredExploreLayers: ExploreLayerInfo[] = [];
  /** Unique import file names for filter dropdown (empty string = import without file) */
  exploreFilesList: string[] = [];
  /** Selected file filter: null = all files, string = only layers from this file */
  selectedExploreFile: string | null = null;
  isLoadingExploreLayers = false;
  exploreError: string | null = null;
  exploreDrawingTypeFilter: string = '';
  polygonInfo: Polygon | null = null;

  // Warning dialog state
  selectedLayerForAction: ExploreLayerInfo | null = null;
  pendingAction: 'reassign' | null = null;
  modifiedObjectsCheck: ModifiedObjectsCheck | null = null;
  isCheckingModified = false;
  pendingLayersToUpdate: ExploreLayerInfo[] = [];
  pendingIncludeModified = false;

  // Operation result
  operationResult: { message: string; success: boolean } | null = null;
  isProcessingOperation = false;

  constructor(
    private readonly objectService: ObjectService,
    private readonly polygonService: PolygonService,
    private readonly logger: LoggerService,
    private readonly cdr: ChangeDetectorRef,
    private readonly router: Router,
    private readonly geometryImportHandoff: GeometryImportHandoffService,
  ) {}

  private lastMappingActive = false;
  /** File name when restored from Import→maps handoff (no File object). */
  private handoffFileName: string | null = null;
  /** Prevent ngOnInit + ngOnChanges double bootstrap wiping a handoff draft. */
  private didBootstrapOpen = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue === true && !changes['isOpen']?.previousValue) {
      this.bootstrapWhenOpen();
    }
    if (changes['isOpen']?.currentValue === false) {
      this.didBootstrapOpen = false;
    }
    if (changes['mode'] && this.isOpen) {
      this.didBootstrapOpen = false;
      this.bootstrapWhenOpen();
    }
    if (changes['hostMap'] || changes['mapHostedMapping'] || changes['isOpen']) {
      void this.syncHostOverlays({ forceFit: true });
    }
    this.emitMappingActiveIfChanged();
  }

  ngOnInit(): void {
    if (this.isOpen) {
      this.bootstrapWhenOpen();
    }
  }

  private bootstrapWhenOpen(): void {
    if (!this.isOpen || this.didBootstrapOpen) return;
    this.didBootstrapOpen = true;
    // Prefer Import-hub handoff draft over a fresh file-step reset.
    if (this.mapHostedMapping && this.mode === 'import' && this.geometryImportHandoff.hasDraft()) {
      void this.loadObjectTypes();
      this.tryRestoreHandoffDraft();
      return;
    }
    this.initializeModal();
  }

  ngOnDestroy(): void {
    this.clearHostOverlays();
    if (this.lastMappingActive) {
      this.mappingActiveChange.emit(false);
      this.lastMappingActive = false;
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onDocumentEscape(event: Event): void {
    if (!this.isMapHostedMappingActive) return;
    event.preventDefault();
    event.stopPropagation();
    this.close();
  }

  goBackToFileStep(): void {
    this.clearHostOverlays();
    this.currentStep = 'file';
    this.emitMappingActiveIfChanged();
  }

  private emitMappingActiveIfChanged(): void {
    const active = this.isMapHostedMappingActive;
    if (active === this.lastMappingActive) return;
    this.lastMappingActive = active;
    this.mappingActiveChange.emit(active);
  }

  private async syncHostOverlays(options?: { forceFit?: boolean }): Promise<void> {
    if (!this.isMapHostedMappingActive || !this.hostMap) {
      this.clearHostOverlays();
      this.emitMappingActiveIfChanged();
      return;
    }

    // Mount side panel first so Leaflet size + fit padding account for it.
    this.emitMappingActiveIfChanged();
    this.cdr.detectChanges();
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

    this.hostOverlay.attach(this.hostMap, {
      maxFeatures: 2000,
      fitPaddingTopLeft: [24, 24],
      fitPaddingBottomRight: [IMPORT_MAPPING_PANEL_WIDTH_PX + 24, 24],
      onLayerActivated: (name) => this.onMapLayerActivated(name),
    });
    this.hostOverlay.setInputs({
      geoJson: this.mapPreviewGeoJson,
      layers: this.layers,
      selectedSourceCrs: this.selectedSourceCrs,
      detectedSourceCrs: this.detectedSourceCrs,
      highlightLayerName: this.previewHighlightLayerName,
    });
    this.hostOverlayHint = 'Připravuji vložení vrstev na mapu…';
    this.cdr.markForCheck();
    // forceFit after CRS/WGS84 paint — overlay host also re-fits once the panel settles.
    await this.hostOverlay.render({ forceFit: options?.forceFit !== false });
    this.hostMap.invalidateSize({ animate: false });
    await new Promise<void>((resolve) => setTimeout(resolve, 160));
    this.hostMap.invalidateSize({ animate: false });
    if (options?.forceFit !== false) {
      this.hostOverlay.fitAllVisible({ animate: true });
    }
    if (this.hostOverlay.isBuildingPreview) {
      this.hostOverlayHint = 'Připravuji vložení vrstev na mapu…';
    } else if (this.hostOverlay.unavailableReason) {
      this.hostOverlayHint = this.hostOverlay.unavailableReason;
    } else if (this.mapPreviewIsPartial || this.hostOverlay.truncated) {
      this.hostOverlayHint = 'Náhled zobrazuje jen část prvků (výkon). Import použije celý soubor.';
    } else {
      this.hostOverlayHint = null;
    }
    this.cdr.markForCheck();
  }

  /** Restore Import-hub analyze handoff onto map-hosted mapping step. */
  private tryRestoreHandoffDraft(): void {
    if (!this.mapHostedMapping || this.mode !== 'import' || !this.isOpen) return;
    const draft = this.geometryImportHandoff.takeDraft();
    if (!draft) return;

    this.handoffFileName = draft.fileName?.trim() || null;
    this.parsedGeoJson = draft.parsedGeoJson as GeoJsonFeatureCollection | null;
    this.previewGeoJson = (draft.previewGeoJson ?? null) as GeoJsonFeatureCollection | null;
    this.pendingDxfText = draft.pendingDxfText;
    this.layers = draft.layers.map((layer) => ({
      ...layer,
      mappedType: layer.mappedType || 'UNASSIGNED',
      visibleOnMap: layer.visibleOnMap !== false,
    }));
    this.totalFeatures = draft.totalFeatures;
    this.detectedSourceCrs = draft.detectedSourceCrs;
    this.selectedSourceCrs = 'auto';
    this.drawingType = draft.drawingType;
    this.selectedPolygonId = draft.selectedPolygonId;
    this.isNewPolygon = draft.isNewPolygon;
    this.kiaCode = draft.kiaCode;
    this.newPolygonName = draft.newPolygonName;
    this.newPolygonKiaCode = draft.newPolygonKiaCode;
    this.newPolygonRealizationYear = draft.newPolygonRealizationYear;
    this.newPolygonFolderNumber = draft.newPolygonFolderNumber;
    this.newPolygonConstructionType = draft.newPolygonConstructionType;
    this.newPolygonExpectedConstructionStart = draft.newPolygonExpectedConstructionStart;
    this.newPolygonPlannedConstructionEnd = draft.newPolygonPlannedConstructionEnd;
    this.newPolygonDescription = draft.newPolygonDescription;
    this.newPolygonResponsibleBranch = draft.newPolygonResponsibleBranch;
    this.newPolygonSapConstruction = draft.newPolygonSapConstruction;
    this.newPolygonProjectState = draft.newPolygonProjectState;
    this.newPolygonStavebnik = draft.newPolygonStavebnik;
    this.newPolygonProjektant = draft.newPolygonProjektant;
    this.newPolygonZhotovitel = draft.newPolygonZhotovitel;
    this.newPolygonStavbyveduciName = draft.newPolygonStavbyveduciName;
    this.newPolygonStavebniPovoleniCislo = draft.newPolygonStavebniPovoleniCislo;
    this.newPolygonObec = draft.newPolygonObec;
    this.previewHighlightLayerName = null;
    this.previewFitLayerName = null;
    this.currentStep = 'mapping';
    this.updateObjectTypesForDrawingType();
    this.ensurePolygonsLoaded();
    void this.syncHostOverlays({ forceFit: true });
    this.cdr.markForCheck();
  }

  private clearHostOverlays(): void {
    this.hostOverlay.detach();
    this.hostOverlayHint = null;
  }

  private initializeModal(): void {
    const lockedId = this.lockPolygonId?.trim() || undefined;
    const id = this.polygonId || lockedId;
    if (this.mode === 'explore' && id) {
      this.currentStep = 'explore';
      this.loadExploreLayers();
      this.loadPolygonInfo();
    } else if (!this.showPolygonPicker && lockedId) {
      this.currentStep = 'file';
      this.selectedPolygonId = lockedId;
      this.isNewPolygon = false;
      this.loadObjectTypes();
      this.loadPolygonInfoForLock(lockedId);
    } else {
      this.currentStep = 'file';
      this.loadData();
    }
  }

  private async loadPolygonInfoForLock(polygonId: string): Promise<void> {
    try {
      const polygon = await firstValueFrom(this.polygonService.getPolygonById(polygonId));
      this.polygonInfo = polygon;
      this.kiaCode = polygon.kiaCode ?? '';
      this.cdr.detectChanges();
    } catch (error) {
      this.logger.error('Failed to load locked polygon:', error);
    }
  }

  private loadData(): void {
    // Polygon list is only needed on the mapping step — defer to keep modal open snappy.
    this.loadObjectTypes();
  }

  // =============================================================================
  // EXPLORE MODE METHODS
  // =============================================================================

  private async loadPolygonInfo(): Promise<void> {
    const id = this.polygonId;
    if (!id) return;
    
    try {
      const polygon = await firstValueFrom(this.polygonService.getPolygonById(id));
      this.polygonInfo = polygon;
      this.cdr.detectChanges();
    } catch (error) {
      this.logger.error('Failed to load polygon info:', error);
    }
  }

  async loadExploreLayers(): Promise<void> {
    const id = this.polygonId;
    if (!id) return;

    this.isLoadingExploreLayers = true;
    this.exploreError = null;

    try {
      const layers = await firstValueFrom(this.objectService.getLayersByPolygon(id, true));
      this.exploreLayers = layers || [];
      
      // Load object types first, then initialize newObjectType
      // This ensures the dropdown has all available options before setting selected value
      await this.loadObjectTypes();
      
      // Update object types first to ensure dropdown has all options
      this.updateObjectTypesForDrawingType();
      
      // Initialize newObjectType with current objectType for each layer
      this.exploreLayers.forEach(layer => {
        layer.newObjectType = layer.objectType || 'UNASSIGNED';
      });
      // Build unique file list for filter (include empty for imports without file)
      const fileSet = new Set<string>();
      this.exploreLayers.forEach(layer => {
        fileSet.add(layer.fileName ?? '');
      });
      this.exploreFilesList = Array.from(fileSet).sort((a, b) => (a || '').localeCompare(b || ''));
      const scoped = this.scopedImportFileName?.trim();
      this.selectedExploreFile = scoped ? scoped : null;
      this.applyExploreFilters();
      this.cdr.markForCheck();
      
      // Debug: check if values match
      this.logger.info('Explore layers loaded:', { 
        count: this.exploreLayers.length,
        layers: this.exploreLayers.map(l => ({ 
          name: l.layerName, 
          objectType: l.objectType, 
          newObjectType: l.newObjectType 
        })),
        objectTypes: this.objectTypes.map(t => ({ value: t.value, label: t.label }))
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Chyba při načítání vrstev';
      this.exploreError = message;
      this.logger.error('Failed to load explore layers:', error);
    } finally {
      this.isLoadingExploreLayers = false;
      this.cdr.detectChanges();
    }
  }

  applyExploreFilters(): void {
    if (this.selectedExploreFile === null) {
      this.filteredExploreLayers = this.exploreLayers;
    } else {
      this.filteredExploreLayers = this.exploreLayers.filter(
        layer => (layer.fileName ?? '') === this.selectedExploreFile
      );
    }
  }

  onExploreFileFilterChange(): void {
    this.applyExploreFilters();
    this.cdr.detectChanges();
  }

  onExploreDrawingTypeFilterChange(): void {
    // Update object types dropdown based on filter
    this.updateObjectTypesForDrawingType();
    this.applyExploreFilters();
    this.cdr.detectChanges();
  }

  compareTypes(a: string, b: string): boolean {
    return a === b;
  }

  getObjectTypeLabel(typeValue: string): string {
    // Empty string or UNASSIGNED should show as "Nepřiřazeno"
    if (typeValue === '' || typeValue === 'UNASSIGNED') {
      return 'Nepřiřazeno';
    }
    
    // Find label from all available types (from backend)
    const found = this.allObjectTypes.find(t => t.value === typeValue);
    if (found) {
      return found.label;
    }
    
    return typeValue || 'Nepřiřazeno';
  }

  // Check if there are any unsaved changes
  hasUnsavedChanges(): boolean {
    return this.exploreLayers.some(layer => 
      layer.newObjectType !== undefined && 
      layer.newObjectType !== (layer.skipped ? 'UNASSIGNED' : layer.objectType)
    );
  }

  // Save all changes at once
  async onSaveAllChanges(): Promise<void> {
    const id = this.polygonId;
    if (!id || !this.hasUnsavedChanges()) return;

    // Get layers to update
    const layersToUpdate = this.exploreLayers.filter(layer => 
      layer.newObjectType !== undefined && 
      layer.newObjectType !== (layer.skipped ? 'UNASSIGNED' : layer.objectType)
    );

    // Check for modified objects in all layers
    let totalModifiedCount = 0;
    const layersWithModified: Array<{ layer: ExploreLayerInfo; check: any }> = [];

    for (const layer of layersToUpdate) {
      try {
        // id is guaranteed to be non-null due to check on line 277
        const check = await firstValueFrom(this.objectService.checkModifiedObjects(id, layer.layerName, layer.fileName));
        if (check.modifiedCount > 0) {
          totalModifiedCount += check.modifiedCount;
          layersWithModified.push({ layer, check });
        }
      } catch (error: unknown) {
        this.logger.warn(`Failed to check modified objects for layer ${layer.layerName}:`, error);
        // Continue checking other layers
      }
    }

    // If there are modified objects, show warning dialog
    if (totalModifiedCount > 0 && layersWithModified.length > 0) {
      // Store all layers to update for later execution
      this.pendingLayersToUpdate = layersToUpdate;
      this.pendingIncludeModified = true;
    
      // Show warning for the first layer with modified objects
      this.selectedLayerForAction = layersWithModified[0].layer;
      this.modifiedObjectsCheck = {
        layerName: layersWithModified[0].layer.layerName,
        totalCount: layersWithModified[0].check.totalCount,
        modifiedCount: totalModifiedCount,
        unmodifiedCount: layersWithModified[0].check.totalCount - totalModifiedCount,
        modifiedObjects: layersWithModified[0].check.modifiedObjects
      };
      this.currentStep = 'warning';
      this.isCheckingModified = false;
      this.cdr.detectChanges();
      return;
    }

    // No modified objects, proceed with save
    await this.executeSaveAllChanges(layersToUpdate, false);
  }

  // Execute saving all changes
  private async executeSaveAllChanges(layersToUpdate: ExploreLayerInfo[], includeModified: boolean): Promise<void> {
    const id = this.polygonId;
    if (!id) {
      this.logger.error('Cannot execute save: polygonId is not set');
      return;
    }

    this.isProcessingOperation = true;
    this.operationResult = null;
    this.currentStep = 'reassigning';

    try {
      let totalUpdated = 0;
      let totalSkipped = 0;

      for (const layer of layersToUpdate) {
        // Use UNASSIGNED if not set or empty
        const newObjectType = (!layer.newObjectType || layer.newObjectType === '') 
          ? 'UNASSIGNED' 
          : layer.newObjectType;
        
        try {
          const result = await firstValueFrom(this.objectService.reassignLayer({
            polygonId: id,
            layerName: layer.layerName,
            newObjectType: newObjectType,
            includeModified: includeModified ? true : undefined,
            importFileName: layer.fileName,
          }));

          totalUpdated += result.updated || 0;
          totalSkipped += result.skipped || 0;
        } catch (error: unknown) {
          this.logger.error(`Failed to reassign layer ${layer.layerName}:`, error);
          // Continue with other layers
        }
      }

      this.operationResult = {
        success: true,
        message: `Uloženo: ${totalUpdated} objektů aktualizováno`
      };

      // Refresh layers list
      await this.loadExploreLayers();
      
      // Close modal and show success message after a short delay
      setTimeout(() => {
        this.close();
      }, 1500);
      
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Operace selhala';
      this.operationResult = {
        success: false,
        message
      };
      this.logger.error('Failed to save changes:', error);
    } finally {
      this.isProcessingOperation = false;
      this.cdr.detectChanges();
    }
  }

  private async checkModifiedObjectsForLayer(layerName: string): Promise<void> {
    const id = this.polygonId;
    if (!id) return;

    this.isCheckingModified = true;
    this.currentStep = 'warning';

    try {
      const check = await firstValueFrom(this.objectService.checkModifiedObjects(id, layerName));
      this.modifiedObjectsCheck = check;
      
      // If no modified objects, proceed directly
      if (check.modifiedCount === 0) {
        await this.executeAction();
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Chyba při kontrole objektů';
      this.exploreError = message;
      this.currentStep = 'explore';
      this.logger.error('Failed to check modified objects:', error);
    } finally {
      this.isCheckingModified = false;
      this.cdr.detectChanges();
    }
  }

  async onWarningConfirm(): Promise<void> {
    // If we have pending layers to update (from onSaveAllChanges), execute them
    if (this.pendingLayersToUpdate.length > 0) {
      await this.executeSaveAllChanges(this.pendingLayersToUpdate, this.pendingIncludeModified);
      this.pendingLayersToUpdate = [];
      this.pendingIncludeModified = false;
    } else {
      // Otherwise, execute single layer action
    await this.executeAction();
    }
  }

  onWarningCancel(): void {
    this.selectedLayerForAction = null;
    this.pendingAction = null;
    this.modifiedObjectsCheck = null;
    this.currentStep = 'explore';
    this.cdr.detectChanges();
  }

  private async executeAction(): Promise<void> {
    const id = this.polygonId;
    if (!id || !this.selectedLayerForAction || !this.pendingAction) return;

    this.isProcessingOperation = true;
    this.operationResult = null;

    try {
      if (this.pendingAction === 'reassign') {
        // Empty string means "Neimportovat" = UNASSIGNED
        const newObjectType = this.selectedLayerForAction.newObjectType === '' 
          ? 'UNASSIGNED' 
          : this.selectedLayerForAction.newObjectType!;
        
        // If we're in warning step, user confirmed to include modified objects
        const includeModified: boolean | undefined = this.currentStep === 'warning' && this.modifiedObjectsCheck && this.modifiedObjectsCheck.modifiedCount > 0 
          ? true 
          : undefined;
        
        // id is guaranteed to be string due to guard clause above
        const result = await firstValueFrom(this.objectService.reassignLayer({
          polygonId: id,
          layerName: this.selectedLayerForAction.layerName,
          newObjectType: newObjectType,
          includeModified,
          importFileName: this.selectedLayerForAction.fileName,
        }));
        
        const actionText = newObjectType === 'UNASSIGNED' 
          ? 'nastavena jako Nepřiřazeno' 
          : 'přeřazena';
        
        this.operationResult = {
          success: true,
          message: `Vrstva ${actionText}: ${result.updated} objektů aktualizováno`
        };
        
        // Close modal and show success message after a short delay
        setTimeout(() => {
          this.close();
        }, 1500);
      }

      // Refresh layers list
      await this.loadExploreLayers();
      
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Operace selhala';
      this.operationResult = {
        success: false,
        message
      };
      this.logger.error('Action failed:', error);
    } finally {
      this.isProcessingOperation = false;
      this.selectedLayerForAction = null;
      this.pendingAction = null;
      this.modifiedObjectsCheck = null;
      this.currentStep = 'explore';
      this.cdr.detectChanges();
    }
  }

  // =============================================================================
  // IMPORT MODE METHODS
  // =============================================================================

  isLoadingPolygons = false;
  private polygonsLoadStarted = false;

  /** Load polygon picker options once when entering the mapping step. */
  private ensurePolygonsLoaded(): void {
    if (!this.showPolygonPicker || this.polygonsLoadStarted) return;
    this.polygonsLoadStarted = true;
    void this.loadPolygons();
  }

  async loadPolygons(): Promise<void> {
    this.isLoadingPolygons = true;
    try {
      const polygons = await firstValueFrom(
        this.polygonService.getPolygonsForPicker({ planState: 'aktivni', limit: 1000 }),
      );
      
      this.polygons = Array.isArray(polygons) ? polygons : [];

      // Picker mode: prefer Input polygonId (e.g. map ?polygon=), else „Nové“.
      // Locked import never reaches here (showPolygonPicker === false).
      if (this.showPolygonPicker) {
        this.applyPreferredPolygonSelection();
      }
      
      this.cdr.detectChanges();
    } catch (error: unknown) {
      this.logger.error('Failed to load polygons:', error);
      this.polygons = [];
      this.setNewPolygon();
      this.cdr.detectChanges();
    } finally {
      this.isLoadingPolygons = false;
    }
  }

  /** Preselect existing polygon when `polygonId` Input matches the loaded list. */
  private applyPreferredPolygonSelection(): void {
    const preferred = (this.polygonId ?? '').trim();
    if (preferred && preferred !== 'new' && this.polygons.some((p) => p.id === preferred)) {
      this.onPolygonIdChange(preferred);
      return;
    }
    this.setNewPolygon();
  }

  private setNewPolygon(): void {
    this.selectedPolygonId = 'new';
    this.isNewPolygon = true;
    this.kiaCode = '';
    this.newPolygonName = '';
    this.newPolygonKiaCode = '';
    this.newPolygonRealizationYear = '';
    this.newPolygonFolderNumber = '';
    this.newPolygonConstructionType = '';
    this.newPolygonExpectedConstructionStart = '';
    this.newPolygonPlannedConstructionEnd = '';
    this.newPolygonDescription = '';
    this.newPolygonResponsibleBranch = '';
    this.newPolygonSapConstruction = '';
    this.newPolygonProjectState = '';
    this.newPolygonStavebnik = DEFAULT_POLYGON_DIARY_STAVEBNIK;
    this.newPolygonProjektant = '';
    this.newPolygonZhotovitel = DEFAULT_POLYGON_DIARY_ZHOTOVITEL;
    this.newPolygonStavbyveduciName = '';
    this.newPolygonStavebniPovoleniCislo = '';
    this.newPolygonObec = '';
  }

  async loadObjectTypes(): Promise<void> {
    this.isLoadingObjectTypes = true;
    try {
      const types = await firstValueFrom(this.objectService.getObjectTypeDefinitions());
      
      // Store all types from backend
      this.allObjectTypes = [];
      if (Array.isArray(types)) {
        for (const type of types) {
          const typeString = type.type as string;
          
          // Use Czech name from backend, fallback to name, then type string
          const label = type.czechName || type.name || typeString;
          
          this.allObjectTypes.push({
            value: typeString,
            label: label
          });
        }
      }
      
      // Always add UNASSIGNED to allObjectTypes (backend filters it out for map layers, but we need it for import modal)
      if (!this.allObjectTypes.some(t => t.value === 'UNASSIGNED')) {
        this.allObjectTypes.push({
          value: 'UNASSIGNED',
          label: 'Nepřiřazeno'
        });
      }
      
      // Update object types based on current drawing type (with filtering)
      this.updateObjectTypesForDrawingType();
      
      this.cdr.detectChanges();
    } catch (error: unknown) {
      this.logger.error('Failed to load object types:', error);
      // Fallback: at least show UNASSIGNED option
      this.objectTypes = [{ value: 'UNASSIGNED', label: 'Nepřiřazeno' }];
      this.cdr.detectChanges();
    } finally {
      this.isLoadingObjectTypes = false;
    }
  }

  /**
   * Update object types based on selected drawing type
   * Filters types according to DRAWING_TYPE_MAPPING
   * UNASSIGNED is always available for both drawing types
   * Preserves already selected types from layers even if they're from different drawing type
   */
  private updateObjectTypesForDrawingType(): void {
    this.objectTypes = [];
    
    // Import: show all types. Explore: use filter if set.
    const filterType = this.mode === 'import' ? '' : this.exploreDrawingTypeFilter;
    
    // Collect already selected types from layers to preserve them in dropdown
    // This allows users to map layers from different drawing types simultaneously
    const selectedTypes = new Set<string>();
    if (this.mode === 'import' && this.layers) {
      this.layers.forEach(layer => {
        if (layer.mappedType) {
          selectedTypes.add(layer.mappedType);
        }
      });
    } else if (this.mode === 'explore' && this.exploreLayers) {
      this.exploreLayers.forEach(layer => {
        if (layer.newObjectType) {
          selectedTypes.add(layer.newObjectType);
        }
      });
    }
    
    // Filter types based on drawing type
    const allowedTypes = new Set<string>();
    if (filterType === '1') {
      // Drawing type 1: Výkop - show only VYKOP types
      DRAWING_TYPE_MAPPING['1'].forEach((type: string) => allowedTypes.add(type));
    } else if (filterType === '2') {
      // Drawing type 2: Instalace - show only installation types
      DRAWING_TYPE_MAPPING['2'].forEach((type: string) => allowedTypes.add(type));
    } else {
      // No filter or 'all' - show all types from both drawing types
      DRAWING_TYPE_MAPPING['1'].forEach((type: string) => allowedTypes.add(type));
      DRAWING_TYPE_MAPPING['2'].forEach((type: string) => allowedTypes.add(type));
    }
    
    // Always include UNASSIGNED in dropdown (available for both drawing types)
    allowedTypes.add('UNASSIGNED');
    
    // Also include already selected types (even if from different drawing type)
    selectedTypes.forEach(type => {
      if (type && type !== '') {
        allowedTypes.add(type);
      }
    });
    
    // Import mapping: replace generic VYKOP with first-class R100 / MET / UR choices.
    const useSegmentKindsInList = this.mode === 'import' && allowedTypes.has('VYKOP');
    const filteredTypes = this.allObjectTypes.filter((type) => {
      if (!allowedTypes.has(type.value)) return false;
      if (useSegmentKindsInList && type.value === 'VYKOP') return false;
      return true;
    });

    const unassignedType = filteredTypes.find((t) => t.value === 'UNASSIGNED');
    const otherTypes = filteredTypes.filter((t) => t.value !== 'UNASSIGNED');

    if (unassignedType) {
      this.objectTypes.push(unassignedType);
    }
    if (useSegmentKindsInList) {
      for (const kind of VYKOP_SEGMENT_KIND_FILTER_ORDER) {
        this.objectTypes.push({
          value: kind,
          label: VYKOP_SEGMENT_KIND_FILTER_LABELS[kind as VykopSegmentKind],
        });
      }
    }
    this.objectTypes.push(...otherTypes);

    // Preserve already-selected segment kinds in the dropdown.
    if (useSegmentKindsInList) {
      for (const selected of selectedTypes) {
        if (!isVykopSegmentKind(selected)) continue;
        if (this.objectTypes.some((t) => t.value === selected)) continue;
        this.objectTypes.push({
          value: selected,
          label: VYKOP_SEGMENT_KIND_FILTER_LABELS[selected as VykopSegmentKind],
        });
      }
    }
  }

  /**
   * Handle drawing type change
   * Keep existing layer mappings - only update available object types in dropdown
   */
  onDrawingTypeChange(): void {
    this.updateObjectTypesForDrawingType();
    // Don't clear mapped types - keep user selections so files with layers in both drawing types work correctly
    this.cdr.detectChanges();
  }

  /** Polygon picker options for `app-filter-multi-select` (search when > 10). */
  get polygonFilterOptions(): FilterMultiOption[] {
    return [
      ...toPolygonFilterOptions(this.polygons),
      { value: 'new', label: 'Nové' },
    ];
  }

  get polygonFilterSelected(): string[] {
    const id = (this.selectedPolygonId ?? '').trim();
    return id ? [id] : [];
  }

  onPolygonFilterChange(ids: string[]): void {
    this.onPolygonIdChange(ids[0] ?? '');
  }

  onPolygonIdChange(polygonId: string): void {
    this.selectedPolygonId = polygonId;
    this.isNewPolygon = polygonId === 'new';
    this.newPolygonName = '';
    this.newPolygonKiaCode = '';
    this.newPolygonRealizationYear = '';
    this.newPolygonFolderNumber = '';
    this.newPolygonConstructionType = '';
    this.newPolygonExpectedConstructionStart = '';
    this.newPolygonPlannedConstructionEnd = '';
    
    if (this.isNewPolygon) {
      this.kiaCode = '';
    } else {
      const polygon = this.polygons.find(p => p.id === polygonId);
      this.kiaCode = polygon?.kiaCode || '';
    }
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];
    const name = file.name.toLowerCase();

    if (isDgnFileName(name)) {
      this.fileError =
        'Soubory DGN (MicroStation V8 / OpenCities Map) zatím nepodporujeme — exportujte do DXF nebo GeoJSON.';
      this.resetFile();
      return;
    }

    if (!this.isValidImportFile(file)) {
      this.fileError = 'Soubor musí být geometrie: GeoJSON (.geojson / .json) nebo DXF (.dxf)';
      this.resetFile();
      return;
    }

    this.fileError = null;
    this.selectedFile = file;
    this.pendingDxfText = null;
    this.parsedGeoJson = null;
    this.previewGeoJson = null;
    this.detectedSourceCrs = null;
    this.selectedSourceCrs = 'auto';

    try {
      if (isDxfFileName(name)) {
        this.pendingDxfText = await this.readDxfText(file);
        this.logger.info('DXF file selected:', file.name);
        return;
      }

      const fileText = await file.text();
      this.parsedGeoJson = JSON.parse(fileText) as GeoJsonFeatureCollection;

      if (this.parsedGeoJson?.type !== 'FeatureCollection') {
        throw new Error('Invalid GeoJSON format. Expected FeatureCollection.');
      }

      this.logger.info('File selected and parsed:', file.name);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Chyba při parsování souboru';
      this.fileError = message;
      this.resetFile();
    }
  }

  private isValidImportFile(file: File): boolean {
    const name = file.name.toLowerCase();
    return (
      name.endsWith('.geojson') ||
      name.endsWith('.json') ||
      name.endsWith('.dxf') ||
      file.type === 'application/json' ||
      file.type === 'application/geo+json'
    );
  }

  /**
   * CAD DXF: UTF-8 (modern exports) or Windows-1250 (ANSI_1250 z CZ CAD).
   * Always decoding as CP1250 corrupts UTF-8 Czech layer names (ATRAKČNÍ → ATRAKÄŚNĂŤ).
   */
  private async readDxfText(file: File): Promise<string> {
    const buffer = await file.arrayBuffer();
    try {
      return decodeCzechTextBuffer(buffer);
    } catch {
      return new TextDecoder('utf-8').decode(buffer);
    }
  }

  private resetFile(): void {
    this.selectedFile = null;
    this.parsedGeoJson = null;
    this.previewGeoJson = null;
    this.pendingDxfText = null;
    this.detectedSourceCrs = null;
    this.selectedSourceCrs = 'auto';
  }

  async analyzeFile(): Promise<void> {
    if (!this.parsedGeoJson && !this.pendingDxfText) {
      this.analysisError = 'Soubor není načten nebo je neplatný';
      return;
    }

    this.isAnalyzing = true;
    this.analysisError = null;

    try {
      let analysis: GeoJsonAnalysisResponse | null = null;

      if (this.pendingDxfText) {
        // DXF still needs server-side conversion; GeoJSON is analyzed locally (no huge POST).
        // Omit sourceCrs so the server auto-detects (prefer S-JTSK / EPSG:5514).
        analysis = (await firstValueFrom(
          this.objectService.analyzeGeoJson(null, {
            dxfData: this.pendingDxfText,
          }),
        )) as GeoJsonAnalysisResponse;
      } else if (this.parsedGeoJson) {
        const local = analyzeGeoJsonClientSide(this.parsedGeoJson, 'auto');
        if (local) {
          analysis = {
            layers: local.layers,
            totalFeatures: local.totalFeatures,
            detectedSourceCrs: local.detectedSourceCrs,
            sourceCrs: local.sourceCrs,
          };
        }
      }

      if (analysis?.layers) {
        this.layers = analysis.layers.map((layer) => ({
          ...layer,
          mappedType: 'UNASSIGNED',
          visibleOnMap: true,
        }));
        this.previewHighlightLayerName = null;
        this.previewFitLayerName = null;
        this.totalFeatures = analysis.totalFeatures || 0;
        this.detectedSourceCrs = analysis.detectedSourceCrs ?? null;
        this.selectedSourceCrs = 'auto';
        this.previewGeoJson =
          analysis.previewGeoJson?.type === 'FeatureCollection' ? analysis.previewGeoJson : null;
        if (analysis.geoJsonData?.type === 'FeatureCollection') {
          this.parsedGeoJson = analysis.geoJsonData;
          // Full conversion returned — import can post GeoJSON and drop the raw DXF.
          this.pendingDxfText = null;
          if (!this.previewGeoJson) this.previewGeoJson = analysis.geoJsonData;
        }
        this.logger.info('Import file analyzed:', {
          layers: this.layers.length,
          features: this.totalFeatures,
          detectedSourceCrs: this.detectedSourceCrs,
        });

        // Off-map entry (Import hub): hand off to /maps side-panel mapping, then return.
        if (!this.mapHostedMapping) {
          this.handoffToMapsForMapping();
          return;
        }

        this.currentStep = 'mapping';
        this.ensurePolygonsLoaded();
        this.updateObjectTypesForDrawingType();
        void this.syncHostOverlays({ forceFit: true });
      } else {
        this.analysisError = 'Nepodařilo se analyzovat soubor';
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Chyba při analýze souboru';
      this.analysisError = message;
      this.logger.error('Analysis error:', error);
    } finally {
      this.isAnalyzing = false;
      this.cdr.markForCheck();
    }
  }

  canProceedToImport(): boolean {
    // Polygon is required; for new polygon, Drive bootstrap fields are required.
    if (!this.selectedPolygonId || this.selectedPolygonId === '') return false;
    if (this.isNewPolygon) return this.newPolygonDriveBootstrapMissing().length === 0;
    return true;
  }

  private newPolygonDriveBootstrapMissing(): string[] {
    const branch = this.newPolygonResponsibleBranch.trim();
    return projectDriveMissingIdentityFields({
      name: this.newPolygonName,
      realizationYear: this.newPolygonRealizationYear,
      responsibleBranch: branch,
      companyId: branch ? 'assumed-from-branch' : null,
      investor: this.newPolygonStavebnik,
    });
  }

  async importGeoJson(): Promise<void> {
    if ((!this.parsedGeoJson && !this.pendingDxfText) || !this.canProceedToImport()) {
      return;
    }

    this.isImporting = true;
    this.importError = null;
    this.importProgress = { imported: 0, total: 0, phase: undefined };
    this.clearHostOverlays();
    this.currentStep = 'importing';
    this.emitMappingActiveIfChanged();

    try {
      // Build layer mapping - include all mappings (UNASSIGNED is valid)
      const layerMapping: Record<string, string> = {};
      const vykopSegmentKindByLayer: Record<string, string> = {};
      for (const layer of this.layers) {
        const mappedType = (layer.mappedType ?? '').trim() || 'UNASSIGNED';
        if (isVykopSegmentKind(mappedType)) {
          layerMapping[layer.layerName] = 'VYKOP';
          vykopSegmentKindByLayer[layer.layerName] = mappedType;
        } else if (mappedType === 'VYKOP') {
          // Legacy: bare VYKOP (no kind in list) → default UR
          layerMapping[layer.layerName] = 'VYKOP';
          vykopSegmentKindByLayer[layer.layerName] = 'UR';
        } else {
          layerMapping[layer.layerName] = mappedType;
        }
      }

      const importBase: ImportRequestBase = {
        drawingType: this.drawingType,
        fileName: this.selectedFile?.name || this.handoffFileName || undefined,
        sourceCrs: this.detectedSourceCrs ?? 'auto',
        // Always send layerMapping (even if empty) to ensure only selected layers are imported
        layerMapping,
        ...(Object.keys(vykopSegmentKindByLayer).length ? { vykopSegmentKindByLayer } : {}),
        // Geometry import must not auto-apply Úřady catalog (backend defaults to apply).
        applyAuthorityCatalog: false,
        applyAuthorityCatalogByPolygon: buildAuthorityCatalogApplyByPolygon(
          this.isNewPolygon ? undefined : this.selectedPolygonId,
          false,
          { createdInJob: this.isNewPolygon },
        ),
      };
      const importData: ImportRequest = this.parsedGeoJson
        ? { ...importBase, geoJsonData: this.parsedGeoJson }
        : { ...importBase, dxfData: this.pendingDxfText! };
      if (this.isNewPolygon) {
        importData.polygonName = this.newPolygonName.trim();
        importData.kiaCode = this.newPolygonKiaCode.trim() || undefined;
        importData.realizationYear = this.newPolygonRealizationYear.trim() || undefined;
        importData.folderNumber = this.newPolygonFolderNumber.trim() || undefined;
        importData.constructionType = this.newPolygonConstructionType.trim() || undefined;
        importData.expectedConstructionStart = this.newPolygonExpectedConstructionStart || undefined;
        importData.plannedConstructionEnd = this.newPolygonPlannedConstructionEnd || undefined;
        importData.description = this.newPolygonDescription.trim() || undefined;
        importData.responsibleBranch = this.newPolygonResponsibleBranch.trim() || undefined;
        importData.sapConstruction = this.newPolygonSapConstruction.trim() || undefined;
        importData.projectState = this.newPolygonProjectState.trim() || undefined;
        importData.stavebnik = this.newPolygonStavebnik.trim() || undefined;
        importData.projektant = this.newPolygonProjektant.trim() || undefined;
        importData.zhotovitel = this.newPolygonZhotovitel.trim() || undefined;
        importData.stavbyveduciName = this.newPolygonStavbyveduciName.trim() || undefined;
        importData.stavebniPovoleniCislo = this.newPolygonStavebniPovoleniCislo.trim() || undefined;
        importData.obec = this.newPolygonObec.trim() || undefined;
      } else {
        importData.polygonId = this.selectedPolygonId;
        importData.kiaCode = this.kiaCode.trim() || undefined;
      }

      this.logger.info('Sending import request:', { 
        drawingType: importData.drawingType, 
        kiaCode: importData.kiaCode,
        polygonId: importData.polygonId,
        polygonName: importData.polygonName,
        fileName: importData.fileName,
        layerMappingKeys: Object.keys(layerMapping)
      });

      const result = await firstValueFrom(
        this.objectService.importGeoJson(importData, {
          onProgress: (imported, total, phase) => {
            this.importProgress = { imported, total, phase };
            this.cdr?.markForCheck();
          }
        })
      ) as ImportResult;
      
      if (result?.statistics) {
        this.importResult = result;
        this.currentStep = 'result';
        this.logger.info('Import completed:', result);
        // Hned po dokončení jobu na serveru (včetně přepočtu hranice/plochy) obnovit mapu — dříve se emitovalo až při zavření modalu → uživatel viděl starou plochu.
        this.importSuccess.emit(result);
        this.cdr?.markForCheck();

        const importedPolygonId = result.polygonId || (!this.isNewPolygon ? this.selectedPolygonId : '');
        if (importedPolygonId) {
          this.logger.info('Triggering address import for polygon:', importedPolygonId);
          this.objectService.importAddressPoints(importedPolygonId).subscribe({
            next: (addressResult) => {
              this.logger.info('Address points imported:', addressResult);
            },
            error: (error) => {
              this.logger.warn('Address import failed (non-critical):', error);
            }
          });
        }
      } else {
        this.importError = 'Import selhal';
      }
    } catch (error: unknown) {
      const errorMessage = this.extractErrorMessage(error);
      this.importError = errorMessage;
      this.logger.error('Import error:', error);
      this.currentStep = 'mapping';
    } finally {
      this.isImporting = false;
    }
  }

  private extractErrorMessage(error: unknown): string {
    if (error && typeof error === 'object') {
      const err = error as { error?: { message?: string }; message?: string };
      return err.error?.message || err.message || 'Chyba při importu';
    }
    if (typeof error === 'string') {
      return error;
    }
    return 'Chyba při importu';
  }

  /**
   * After analyze outside /maps: stash draft, open map-hosted mapping, preserve returnUrl.
   */
  private handoffToMapsForMapping(): void {
    const returnUrl = this.router.url;
    const polygonForQuery =
      (this.lockPolygonId || this.selectedPolygonId || this.polygonId || '').trim() || undefined;

    this.geometryImportHandoff.stash({
      returnUrl: isSafeAppReturnUrl(returnUrl) ? returnUrl : null,
      lockPolygonId: this.lockPolygonId?.trim() || undefined,
      preferredPolygonId: polygonForQuery,
      fileName: this.selectedFile?.name,
      parsedGeoJson: this.parsedGeoJson,
      previewGeoJson: this.previewGeoJson,
      pendingDxfText: this.pendingDxfText,
      layers: this.layers.map((layer) => ({ ...layer })),
      totalFeatures: this.totalFeatures,
      detectedSourceCrs: this.detectedSourceCrs,
      selectedSourceCrs: this.selectedSourceCrs,
      drawingType: this.drawingType,
      selectedPolygonId: this.selectedPolygonId,
      isNewPolygon: this.isNewPolygon,
      kiaCode: this.kiaCode,
      newPolygonName: this.newPolygonName,
      newPolygonKiaCode: this.newPolygonKiaCode,
      newPolygonRealizationYear: this.newPolygonRealizationYear,
      newPolygonFolderNumber: this.newPolygonFolderNumber,
      newPolygonConstructionType: this.newPolygonConstructionType,
      newPolygonExpectedConstructionStart: this.newPolygonExpectedConstructionStart,
      newPolygonPlannedConstructionEnd: this.newPolygonPlannedConstructionEnd,
      newPolygonDescription: this.newPolygonDescription,
      newPolygonResponsibleBranch: this.newPolygonResponsibleBranch,
      newPolygonSapConstruction: this.newPolygonSapConstruction,
      newPolygonProjectState: this.newPolygonProjectState,
      newPolygonStavebnik: this.newPolygonStavebnik,
      newPolygonProjektant: this.newPolygonProjektant,
      newPolygonZhotovitel: this.newPolygonZhotovitel,
      newPolygonStavbyveduciName: this.newPolygonStavbyveduciName,
      newPolygonStavebniPovoleniCislo: this.newPolygonStavebniPovoleniCislo,
      newPolygonObec: this.newPolygonObec,
    });

    this.closeEvent.emit();
    void this.router.navigate(['/maps'], {
      queryParams: {
        geometryImport: '1',
        ...(polygonForQuery ? { polygon: polygonForQuery } : {}),
        ...(isSafeAppReturnUrl(returnUrl) ? { returnUrl } : {}),
      },
    });
  }

  close(): void {
    // importSuccess se emituje už po dokončení importu (viz importGeoJson), aby se mapa obnovila bez čekání na zavření modalu.
    this.clearHostOverlays();
    this.reset();
    this.emitMappingActiveIfChanged();
    this.closeEvent.emit();
  }

  finish(): void {
    // close() will emit importSuccess when currentStep === 'result'
    this.close();
  }

  reset(): void {
    this.clearHostOverlays();
    this.currentStep = this.mode === 'explore' ? 'explore' : 'file';
    this.resetFile();
    this.handoffFileName = null;
    this.previewHighlightLayerName = null;
    this.previewFitLayerName = null;
    this.hostOverlayHint = null;
    this.fileError = null;
    this.analysisError = null;
    this.layers = [];
    this.totalFeatures = 0;
    this.drawingType = '1';
    this.importResult = null;
    this.importError = null;
    this.operationResult = null;
    this.polygonsLoadStarted = false;
    this.setNewPolygon();
  }

  getModalTitle(): string {
    if (this.mode === 'explore') {
      const id = this.polygonId;
      const label = getPolygonDisplayLabel(this.polygonInfo) || id;
      return `Vrstvy polygonu: ${label}`;
    }
    return 'Geometrie';
  }
}
